<template>
  <div :class="commonBackgroundClasses">
    <ClientOnly>
      <div
        v-if="isPhotoBackground"
        class="common-background__images"
      >
        <AnimatedBox
          :key="`${backgroundAnimationKey}-pattern-left`"
          preset="fly-in-left-soft"
          appear
        >
          <img
            :src="BackgroundPatternLeft"
            alt="background"
            class="common-background__image-left"
          >
        </AnimatedBox>

        <AnimatedBox
          :key="`${backgroundAnimationKey}-pattern-right`"
          preset="fly-in-right-soft"
          appear
        >
          <img
            :src="BackgroundPatternRight"
            alt="background"
            class="common-background__image-right"
          >
        </AnimatedBox>

        <AnimatedBox
          :key="`${backgroundAnimationKey}-akinfeev-right`"
          preset="fly-in-right-soft"
          appear
        >
          <img
            :src="BackgroundAkinfeevR"
            alt="background"
            class="common-background__image-right"
          >
        </AnimatedBox>

        <AnimatedBox
          :key="`${backgroundAnimationKey}-akinfeev-left`"
          preset="fly-in-left-soft"
          appear
        >
          <img
            :src="BackgroundAkinfeevL"
            alt="background"
            class="common-background__image-left"
          >
        </AnimatedBox>

        <BgPatternLeft
          v-if="!isStartBackground"
          class="common-background__pattern-left"
        />
        <BgPatternLeft
          v-if="!isStartBackground"
          class="common-background__pattern-right"
        />
        <BgPatternLeftMobile
          v-if="isStartBackground"
          class="common-background__pattern-left-mobile"
        />

        <img
          :class="commonBackgroundClassesImageRightMobile"
          :src="BackgroundAkinfeevRMobile"
          alt="background"
        >

        <AnimatedBox
          v-if="isStartBackground"
          :key="`${backgroundAnimationKey}-congratulations`"
          preset="fade"
          appear
        >
          <img
            :src="CongratulationsImage"
            alt="background"
            class="common-background__image-congratulations"
          >
        </AnimatedBox>

        <AnimatedBox
          preset="banner-slide-vertical"
          mode="out-in"
          appear
        >
          <img
            v-if="isStartBackground"
            :src="StartJerseyImage"
            alt="background"
            class="common-background__image-jersey"
          >
        </AnimatedBox>

        <img
          :src="NoiseImage"
          alt="background"
          class="common-background__image-noise"
        >
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import BackgroundPatternLeft from '@/assets/images/layout/pattern-l.webp';
import BackgroundPatternRight from '@/assets/images/layout/pattern-r.webp';
import BackgroundAkinfeevL from '@/assets/images/layout/akinfeev-l.webp';
import BackgroundAkinfeevR from '@/assets/images/layout/akinfeev-r.webp';
import BackgroundAkinfeevRMobile from '@/assets/images/layout/akinfeev-r-mobile.webp';
import CongratulationsImage from '@/assets/images/layout/congratulations.webp';
import StartJerseyImage from '@/assets/images/layout/start-jersey.webp';
import NoiseImage from '@/assets/images/layout/noise.webp';

const route = useRoute();

const isPhotoBackgroundPath = (path: string) => {
  return path.includes('/team')
    || path.includes('/rules')
    || path.includes('/prizes')
    || path.includes('/start');
};

const backgroundAnimationVersion = ref(0);
const backgroundAnimationKey = computed(() => String(backgroundAnimationVersion.value));

const isPhotoBackground = computed(() => {
  return isPhotoBackgroundPath(route.path);
});

const isStartBackground = computed(() => {
  const path = route.path;
  return path.includes('/start');
});

const isRulesBackground = computed(() => {
  const path = route.path;
  return path.includes('/rules');
});

const commonBackgroundClasses = computed(() => [
  'common-background',
  { 'common-background--photo': isPhotoBackground.value },
  { 'common-background--start': isStartBackground.value },
]);

const commonBackgroundClassesImageRightMobile = computed(() => [
  'common-background__image-right-mobile',
  { 'common-background__image-right-mobile--rules': isRulesBackground.value },
]);

watch(
  () => route.path,
  (newPath, oldPath) => {
    const isNextPhoto = isPhotoBackgroundPath(newPath);
    if (!isNextPhoto) return;

    const wasPrevPhoto = oldPath ? isPhotoBackgroundPath(oldPath) : false;
    if (!wasPrevPhoto) backgroundAnimationVersion.value += 1;
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.common-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/images/layout/background.webp');
  background-size: cover;
  background-position: center;
  z-index: $z-underground;

  @include layout-aspect-mobile {
    background-image: url('@/assets/images/layout/background-mobile.webp');
  }

  // &--photo {
  //   background-image: url('@/assets/images/layout/background-photo.webp');

  //   @include layout-aspect-mobile {
  //     background-image: url('@/assets/images/layout/background-photo-mobile.webp');
  //   }
  // }

  // &--start {
  //   background-image: url('@/assets/images/layout/background-start.webp');
  //   object-fit: cover;

  //   @include layout-aspect-mobile {
  //     background-image: url('@/assets/images/layout/background-start-mobile.webp');
  //   }
  // }

  &__images {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: $z-default;
  }

  &__image-left {
    height: 100%;
    object-fit: cover;
    position: absolute;
    left: 0;
    bottom: 0;

    @include layout-aspect-mobile {
      display: none;
    }
  }

  &__image-right {
    height: 100%;
    position: absolute;
    right: 0;
    bottom: 0;

    @include layout-aspect-mobile {
      display: none;
    }

    &--rules {
      @include layout-aspect-mobile {
        display: none;
      }
    }
  }

  &__pattern-left {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 12rem;
    height: 18.3rem;
    z-index: $z-underground;
    display: none;

    @include layout-aspect-mobile {
      display: block;
    }
  }

  &__pattern-left-mobile {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 39rem;
    height: 27.8rem;
    z-index: $z-underground;
    display: none;

    @include layout-aspect-mobile {
      display: block;
    }
  }

  &__pattern-right {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 12rem;
    height: 18.3rem;
    z-index: $z-underground;
    display: none;
    transform: rotateY(180deg);

    @include layout-aspect-mobile {
      display: block;
    }
  }

  &__image-right-mobile {
    width: 14.369rem;
    object-fit: cover;
    position: absolute;
    right: 0;
    bottom: 0;
    display: none;

    @include layout-aspect-mobile {
      display: block;
    }

    &--rules {
      @include layout-aspect-mobile {
        display: none;
      }
    }
  }

  &__image-congratulations {
    width: 84.2266rem;
    // height: 24.0164rem;
    object-fit: cover;
    position: absolute;
    top: 8.0rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: $z-underground;
    // display: none;

    @include layout-aspect-mobile {
      width: 70.2266rem;
    }
  }

  &__image-jersey {
    width: 27.4244rem;
    object-fit: cover;
    position: absolute;
    bottom: 0;
    left: 26.9rem;

    @include layout-aspect-mobile {
      left: 0;
      transform: translateX(-20%);
    }
  }

  &__image-noise {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.2;
    z-index: $z-default;
  }
}
</style>
